import { AltRoute as AlternativesIcon, CompareArrowsOutlined as ReplaceIcon } from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import omit from "lodash/omit";
import type { FC } from "react";
import { useState } from "react";
import type { ProductResponse } from "../../admin/products/productsApi";
import { useProducts } from "../../admin/products/useProducts";
import CancelButton from "../../components/cancelButton/CancelButton";
import ProductDetails, { type Product } from "../../components/productDetails/ProductDetails";
import type { Currency } from "../../data/BaseEntity";
import type { PriceValue } from "../../misc/price";
import type { CalculationProduct } from "../calculationTypes";
import { AlternativeProductFromMapDialog } from "./AlternativeProductFromMapDialog";
import type { ProductAlternative } from "./useCalculationProductAlternatives";
import { useCalculationProductAlternatives } from "./useCalculationProductAlternatives";

const AlternativeProductsButton: FC<{ onClick: () => void }> = ({ onClick }) => (
  <Tooltip title="Dostępne są alternatywne produkty">
    <IconButton size="small" onClick={onClick} aria-label="Alternatywne produkty">
      <AlternativesIcon fontSize="inherit" />
    </IconButton>
  </Tooltip>
);

const ReplaceButton: FC<{ onClick: () => void }> = ({ onClick }) => (
  <Button startIcon={<ReplaceIcon />} size="small" onClick={onClick}>
    Zastąp
  </Button>
);

type ProductAlternativeWithDetails = ProductAlternative & {
  readonly details: {
    name: string | undefined;
    description: ProductResponse["description"] | undefined;
    contact: ProductResponse["contact"] | undefined;
    images: ProductResponse["images"] | undefined;
  };
};

interface AlternativeProductsTableRowProps {
  readonly value: ProductAlternativeWithDetails;
  readonly currency: Currency | undefined;
  readonly readonly: boolean;
  readonly onReplace: () => void;
}

const AlternativeProductsTableRow: FC<AlternativeProductsTableRowProps> = ({
  value,
  currency,
  readonly,
  onReplace,
}) => (
  <TableRow>
    <TableCell>{value.details.name}</TableCell>
    <TableCell>
      <ProductDetails
        product={value.details.contact && value.details.images ? (value.details as Product) : undefined}
      />
    </TableCell>
    <TableCell>{`${value.doubleTwinPrice.value} ${currency}`}</TableCell>
    <TableCell>{!readonly && <ReplaceButton onClick={() => onReplace()} />}</TableCell>
  </TableRow>
);

interface AlternativeProductsTableProps {
  readonly values: ProductAlternativeWithDetails[];
  readonly currency: Currency | undefined;
  readonly readonly: boolean;
  readonly selectedProductDoubleTwinPrice: PriceValue | null;
  readonly onReplace: (replacement: CalculationProduct) => void;
}

const AlternativeProductsTable: FC<AlternativeProductsTableProps> = ({
  values,
  currency,
  readonly,
  selectedProductDoubleTwinPrice,
  onReplace,
}) => (
  <>
    {selectedProductDoubleTwinPrice !== null && (
      <Typography
        variant="body2"
        sx={{
          color: "text.secondary",
          mb: 1,
        }}
      >
        Wybrany produkt (DOUBLE/TWIN): {selectedProductDoubleTwinPrice} {currency}
      </Typography>
    )}
    <Table>
      <TableHead>
        <TableRow>
          <TableCell>Produkt alternatywny</TableCell>
          <TableCell>Szczegóły</TableCell>
          <TableCell>Cena (DOUBLE/TWIN)</TableCell>
          <TableCell />
        </TableRow>
      </TableHead>
      <TableBody>
        {values.map((p) => {
          return (
            <AlternativeProductsTableRow
              key={p.product.id}
              value={p}
              currency={currency}
              readonly={readonly}
              onReplace={() => onReplace(omit(p.product, "name"))}
            />
          );
        })}
      </TableBody>
    </Table>
  </>
);

interface Props {
  readonly readonly?: boolean;
  readonly selectedProduct: CalculationProduct;
  readonly calculationCurrency: Currency | undefined;
  readonly calculationMargin: number | undefined;
  readonly participantsNumber: number;
  readonly onReplace: (replacement: CalculationProduct) => void;
}

const CalculationProductAlternatives: FC<Props> = ({
  readonly,
  selectedProduct,
  calculationCurrency,
  calculationMargin,
  participantsNumber,
  onReplace,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);
  const { products } = useProducts();
  const { alternatives, selectedProductDoubleTwinPrice } = useCalculationProductAlternatives(
    selectedProduct,
    calculationCurrency ?? "EUR",
    calculationMargin ?? 0,
    products,
    participantsNumber,
  );
  if (alternatives.length === 0) {
    return null;
  }
  const alternativeProducts = alternatives.reduce(
    (acc, it) => ({
      ...acc,
      [it.product.id]: products.find((p) => p.id == it.product.id),
    }),
    {} as Record<string, ProductResponse | undefined>,
  );
  const alternativesWithDetails = alternatives.map((a) => {
    const matchingProduct = alternativeProducts[a.product.id];
    return {
      ...a,
      details: {
        name: matchingProduct?.name,
        description: matchingProduct?.description,
        contact: matchingProduct?.contact,
        images: matchingProduct?.images,
      },
    };
  });
  const selectedPriceLabel = selectedProductDoubleTwinPrice !== null ? selectedProductDoubleTwinPrice.value : null;
  const title = readonly ? "Dostępne są alternatywne produkty" : "Wybierz alternatywny produkt";
  return (
    <>
      <AlternativeProductsButton onClick={() => setDialogOpen(true)} />
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="lg">
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          <AlternativeProductsTable
            values={alternativesWithDetails}
            currency={calculationCurrency}
            readonly={readonly ?? false}
            selectedProductDoubleTwinPrice={selectedPriceLabel}
            onReplace={(p) => onReplace(p)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMapOpen(true)}>Zobacz na mapie</Button>
          <Box sx={{ flex: "1 0 0" }} />
          <CancelButton onClick={() => setDialogOpen(false)}>Zamknij</CancelButton>
        </DialogActions>
      </Dialog>
      <AlternativeProductFromMapDialog
        open={mapOpen}
        products={Object.values(alternativeProducts).filter((p): p is ProductResponse => !!p)}
        onClose={() => setMapOpen(false)}
        onReplace={
          readonly
            ? undefined
            : (product, date) => {
                const alternative = alternativesWithDetails.find((alt) => alt.product?.id === product.id);
                if (alternative) {
                  onReplace({
                    ...alternative.product,
                    date: date ?? alternative.product.date,
                  });
                }
                setMapOpen(false);
                setDialogOpen(false);
              }
        }
      />
    </>
  );
};

export default CalculationProductAlternatives;
